package horcrux.common.types

import arrow.core.Either
import arrow.core.NonEmptyList
import arrow.core.NonEmptySet
import arrow.core.nonEmptySetOf
import arrow.core.raise.either
import horcrux.common.extensions.toNonEmptyMap
import horcrux.common.utils.getOrThrow
import horcrux.common.utils.raiseError

class NonEmptyMap<A, B> private constructor(
    override val entries: NonEmptySet<Map.Entry<A, B>>
) : Map<A, B> by entries.associateBy({ it.key }, { it.value }) {
    override fun toString(): String = "NonEmptyMap<${entries.joinToString { "${it.key}: ${it.value}, " }}>"

    operator fun plus(element: Pair<A, B>): NonEmptyMap<A, B> =
        nonEmptyMapOf(element, *entries.map { it.toPair() }.toTypedArray())

    fun toNonEmptyList(): NonEmptyList<Pair<A, B>> = entries.map { it.toPair() }.toNonEmptyList()

    fun safeGet(key: A): Either<Throwable, B> = either {
        this@NonEmptyMap[key] ?: raiseError("$key does not exist in ${this@NonEmptyMap}")
    }

    fun unsafeGet(key: A): B = safeGet(key).getOrThrow()

    fun replace(key: A, value: B): NonEmptyMap<A, B> =
        nonEmptyMapOf(key to value, *this.filter { it.key != key }.toList().toTypedArray())

    fun safeRemove(key: A): Either<Throwable, NonEmptyMap<A, B>> =
        this.filter { it.key != key }.toNonEmptyMap()

    fun unsafeRemove(key: A): NonEmptyMap<A, B> =
        safeRemove(key).getOrNull() ?: error("Removing $key from $this would result in an empty map")

    companion object {
        private fun <A, B> Pair<A, B>.toMapEntry(): Map.Entry<A, B> {
            return object : Map.Entry<A, B> {
                override val key: A = this@toMapEntry.first
                override val value: B = this@toMapEntry.second
            }
        }

        fun <A, B> nonEmptyMapOf(first: Pair<A, B>, vararg rest: Pair<A, B>): NonEmptyMap<A, B> {
            val initialEntry = first.toMapEntry()
            val otherEntries = rest.map { (it.first to it.second).toMapEntry() }
            val nonEmptySet = nonEmptySetOf(initialEntry, *otherEntries.toTypedArray())
            return NonEmptyMap(nonEmptySet)
        }
    }
}
